export type AvatarFactoryLanguage = 'zh' | 'en';
export type SourceStageKey = 'accepted' | 'converting' | 'chunking' | 'distilling' | 'selfReview' | 'ready';

export const SOURCE_STAGE_KEYS: SourceStageKey[] = [
  'accepted',
  'converting',
  'chunking',
  'distilling',
  'selfReview',
  'ready',
];

export const AVATAR_FACTORY_COPY = {
  zh: {
    languageToggle: 'English',
    page: {
      eyebrow: '猴毛分身工作台',
      title: '猴毛分身工厂',
      description: '把资料自动切片、提炼成私有分身草稿，发布前可直接预览对话、定时任务和调用表现。',
      currentDraftStatus: '当前分身草稿状态',
      currentDraft: '当前分身草稿',
      draftPrefix: '分身：',
      revisionPrefix: '版本',
      loading: '正在加载当前分身草稿...',
      missingSession: '缺少管理员会话，请重新登录。',
      loadFallback: '加载猴毛分身工厂草稿失败。',
      retry: '重试',
      editorAria: '分身工厂编辑区',
      sectionsAria: '分身工厂分区',
      build: '构建',
      draftControls: '分身草稿控制台',
      sourceTab: '资料上传',
      identityTab: '灵魂精雕',
    },
    common: {
      autosaveSaving: '自动保存中',
      autosaveSaved: '已自动保存',
      saving: '保存中',
      saved: '已保存',
      saveFailed: '保存失败',
      versions: '版本',
      versionsSaved: (count: number) => `${count} 个已保存`,
      versionName: '版本名称',
      saveNamedVersion: '保存命名版本',
      restoreVersion: (name: string) => `恢复 ${name}`,
      noSavedVersions: '暂无保存版本。',
      versionSaved: '版本已保存。',
      versionRestored: '版本已恢复，并已创建恢复前安全版本。',
      empty: '空',
      ready: '就绪',
      processing: '处理中',
      waiting: '等待资料',
    },
    statusLabels: {
      idle: '空闲',
      ready: '就绪',
      processing: '处理中',
      generated: '已生成',
      failed: '失败',
      pending: '等待中',
      fallback: '已生成',
    },
    source: {
      input: '输入',
      title: '资料上传',
      draftOnly: '仅草稿',
      uploadSourceFile: '上传分身素材文件',
      uploadHint: '支持批量上传 TXT、Markdown、PDF、DOCX',
      uploadBrowseHint: '拖拽文件到这里，或点击浏览本地文件',
      uploadSizeExceeded: (limit: string) => `超出文件大小限制，单次上传最大 ${limit}。`,
      processingStagesAria: '分身素材处理阶段',
      pipeline: '分身素材流水线',
      stages: {
        accepted: '已接收',
        converting: '转换中',
        chunking: '切片中',
        distilling: '提炼中',
        selfReview: '自检中',
        ready: '就绪',
      },
      publishTitle: '发布为可调用分身',
      publishDescription: '处理完成后写入分身注册表和运行时 artifact。网页入口可用 profile 调用；勾选后 wx-adapter 会通过微信默认入口调用这个分身。',
      publishWithWechat: '同步为微信默认分身',
      publishAction: '发布分身',
      publishing: '发布中',
      publishReady: '已发布',
      publishResult: (profileId: string) => `已发布 profile：${profileId}`,
      publishFallback: '发布分身失败。',
      markdownPreview: '转换后的 Markdown 预览',
      converted: '已转换',
      agentName: '分身名称',
      greeting: '打招呼内容',
      scheduleLabel: '定时任务名称',
      scheduleCron: 'Cron 表达式',
      schedulePrompt: '定时任务提示词',
      scheduleTime: '每天时间',
      scheduleTaskType: '任务类型',
      scheduleMessageContent: '消息内容',
      taskTypes: {
        sendMessage: '发送消息',
        editMessage: '编辑消息内容',
        dailyDigest: '生成日报',
      },
      requestFallback: '素材设置请求失败。',
    },
    identity: {
      eyebrow: '分身',
      title: '灵魂精雕',
      editable: '可编辑',
      generated: '已生成',
      evidence: '证据',
      noEvidence: '暂无关联证据。',
      reviewNotes: '自检备注',
      noReviewNotes: '暂无自检备注。',
      layers: {
        soul: '灵魂 SOUL',
        style: '风格 STYLE',
        memory: '记忆 MEMORY',
        skill: '技能 SKILL',
      },
      autosaveFallback: '自动保存失败。',
      restoreFallback: '恢复版本失败。',
    },
    preview: {
      aria: '分身预览对话',
      eyebrow: '预览',
      title: '预览对话',
      statusAria: '预览状态',
      revision: (revision: number) => `预览版本 ${revision}`,
      generationStatus: (status: string) => `生成状态 ${status}`,
      simulatedSchedule: '模拟定时任务',
      agentSpeaker: '分身',
      userSpeaker: '你',
      messageLabel: '预览消息',
      placeholder: '向当前分身草稿提一个问题。',
      sending: '发送中...',
      send: '发送',
      simulateNow: '立即模拟',
      simulating: '模拟中...',
      helper: '只使用当前预览定时任务设置。',
      refreshFallback: '预览流结束后刷新分身草稿失败。',
      simulateFallback: '模拟预览定时任务失败。',
    },
  },
  en: {
    languageToggle: '中文',
    page: {
      eyebrow: 'Houmao clone workbench',
      title: 'Houmao Clone Factory',
      description:
        'Convert source material into a private clone draft, then validate chat, schedule, and runtime behavior before release.',
      currentDraftStatus: 'Current clone draft status',
      currentDraft: 'Current clone draft',
      draftPrefix: 'Clone: ',
      revisionPrefix: 'rev',
      loading: 'Loading current clone draft...',
      missingSession: 'Admin session is missing. Please sign in again.',
      loadFallback: 'Failed to load Houmao clone factory draft.',
      retry: 'Retry',
      editorAria: 'Clone factory editor',
      sectionsAria: 'Clone factory sections',
      build: 'Build',
      draftControls: 'Clone draft controls',
      sourceTab: 'Data Upload',
      identityTab: 'Soul Refinement',
    },
    common: {
      autosaveSaving: 'Autosave saving',
      autosaveSaved: 'Autosave saved',
      saving: 'Saving',
      saved: 'Saved',
      saveFailed: 'Save failed',
      versions: 'Versions',
      versionsSaved: (count: number) => `${count} saved`,
      versionName: 'Version name',
      saveNamedVersion: 'Save named version',
      restoreVersion: (name: string) => `Restore ${name}`,
      noSavedVersions: 'No saved versions yet.',
      versionSaved: 'Version saved.',
      versionRestored: 'Version restored. A pre-restore safety version was saved.',
      empty: 'Empty',
      ready: 'Ready',
      processing: 'Processing',
      waiting: 'Waiting',
    },
    statusLabels: {
      idle: 'idle',
      ready: 'ready',
      processing: 'processing',
      generated: 'generated',
      failed: 'failed',
      pending: 'pending',
      fallback: 'generated',
    },
    source: {
      input: 'Input',
      title: 'Data Upload',
      draftOnly: 'Draft only',
      uploadSourceFile: 'Upload clone source files',
      uploadHint: 'Batch upload TXT, Markdown, PDF, DOCX',
      uploadBrowseHint: 'Drop files here, or click to browse your computer',
      uploadSizeExceeded: (limit: string) => `File size limit exceeded. Maximum upload size is ${limit}.`,
      processingStagesAria: 'Clone source processing stages',
      pipeline: 'Clone source pipeline',
      stages: {
        accepted: 'Accepted',
        converting: 'Converting',
        chunking: 'Chunking',
        distilling: 'Distilling',
        selfReview: 'Self-review',
        ready: 'Ready',
      },
      publishTitle: 'Publish callable clone',
      publishDescription:
        'Writes the processed draft into the clone registry and runtime artifact store. Web can call it by profile; wx-adapter can call it through the WeChat default entrypoint when enabled.',
      publishWithWechat: 'Make default WeChat clone',
      publishAction: 'Publish clone',
      publishing: 'Publishing',
      publishReady: 'Published',
      publishResult: (profileId: string) => `Published profile: ${profileId}`,
      publishFallback: 'Failed to publish clone.',
      markdownPreview: 'Converted Markdown preview',
      converted: 'Converted',
      agentName: 'Clone name',
      greeting: 'Greeting',
      scheduleLabel: 'Schedule label',
      scheduleCron: 'Schedule cron',
      schedulePrompt: 'Schedule prompt',
      scheduleTime: 'Daily time',
      scheduleTaskType: 'Task type',
      scheduleMessageContent: 'Message content',
      taskTypes: {
        sendMessage: 'Send message',
        editMessage: 'Edit message content',
        dailyDigest: 'Generate daily digest',
      },
      requestFallback: 'Source setup request failed.',
    },
    identity: {
      eyebrow: 'Clone',
      title: 'Soul Refinement',
      editable: 'Editable',
      generated: 'Generated',
      evidence: 'Evidence',
      noEvidence: 'No evidence linked yet.',
      reviewNotes: 'Review notes',
      noReviewNotes: 'No review notes.',
      layers: {
        soul: 'SOUL',
        style: 'STYLE',
        memory: 'MEMORY',
        skill: 'SKILL',
      },
      autosaveFallback: 'Autosave failed.',
      restoreFallback: 'Failed to restore version.',
    },
    preview: {
      aria: 'Clone preview chat',
      eyebrow: 'Preview',
      title: 'Clone Preview',
      statusAria: 'Preview status',
      revision: (revision: number) => `Preview revision ${revision}`,
      generationStatus: (status: string) => `Generation status ${status}`,
      simulatedSchedule: 'Simulated schedule',
      agentSpeaker: 'Clone',
      userSpeaker: 'You',
      messageLabel: 'Preview message',
      placeholder: 'Ask the current clone draft a question.',
      sending: 'Sending...',
      send: 'Send',
      simulateNow: 'Simulate now',
      simulating: 'Simulating...',
      helper: 'Uses current preview schedule settings only.',
      refreshFallback: 'Failed to refresh draft after preview stream.',
      simulateFallback: 'Failed to simulate preview schedule.',
    },
  },
} as const;

export type AvatarFactoryCopy = (typeof AVATAR_FACTORY_COPY)[AvatarFactoryLanguage];

export function translateAvatarFactoryStatus(copy: AvatarFactoryCopy, status: string): string {
  return copy.statusLabels[status as keyof typeof copy.statusLabels] ?? status;
}
